import { axios } from './axiosClient';
import { validate } from '@/utils/errors/helpers';
import type { Division } from '@/types/Division';
import type { DocumentType } from '@/types/DocumentTypes';
import { getDivisionsSchema } from '@/types/schemas/documents/getDivisions';
import { getDocumentTypesSchema } from '@/types/schemas/documents/getDocumentTypes';
import type { DocumentStatus } from '@/types/DocumentStatus';
import { getDocumentStatusesSchema } from '@/types/schemas/documents/getDocumentStatuses';
import type { PaymentType } from '@/types/PaymentType';
import { getPaymentTypesSchema } from '@/types/schemas/documents/getPaymentTypes';
import type { Device } from '@/types/Device';
import { getDevicesSchema } from '@/types/schemas/documents/getDevices';
import type { DeviceConfiguration } from '@/types/DeviceConfiguration';
import { getDeviceConfigurationsSchema } from '@/types/schemas/documents/getDeviceConfigurations';
import type { DeliveryType } from '@/types/DeliveryTypes';
import { getDeliveryTypesSchema } from '@/types/schemas/documents/getDeliveryTypes';
import type { DocumentSeries } from '@/types/DocumentSeries';
import { getDocumentSeriesSchema } from '@/types/schemas/documents/getDocumentSeries';
import { getDocumentSeriesTypesSchema } from '@/types/schemas/documents/getDocumentSeriesTypes';
import type { DocumentSeriesType } from '@/types/DocumentSeriesTypes';
import { getSalesDocumentsWithConditionsSchema } from '@/types/schemas/documents/getSalesDocumentsWithConditions';
import type {
  ExpensesAndCharges,
  MyDataDocumentHeader,
  MyDataDocumentsFiltersData,
  SalesDocument,
  SalesDocumentForGrid,
  SalesDocumentsFiltersData,
} from '@/types/Documents';
import { datepickerToServerDateFormat, parseDateManually } from '@/utils/date';
import { getSalesDocumentByIdSchema } from '@/types/schemas/documents/getSalesDocumentById';
import { getExpensesAndChargesSchema } from '@/types/schemas/documents/getExpensesAndCharges';
import { getDocumentTypeColumnDefinitionFieldsSchema } from '@/types/schemas/documents/getDocumentTypeColumnDefinitions';
import { RecordMode } from '@/types/Base';
import { getDeviceConfigurationBaseSchema } from '@/types/schemas/documents/getAllDeviceConfiguations';
import { getDocumentSeriesTypesBaseSchema } from '@/types/schemas/documents/getAllDocumentSeriesTypes';
import { getMyDataDocumentsSchema } from '@/types/schemas/documents/getMyDataDocuments';
import type { DistributionChannel } from '@/types/DistributionChannel';
import { getDistributionChannelsSchema } from '@/types/schemas/documents/getDistributionChannels';

export async function getDivisions(): Promise<Division[]> {
  const response = await axios.get('/v2/documents/divisions');
  return validate(getDivisionsSchema, response.data.data);
}

export async function addOrEditDivisions(item: Division) {
  const response = await axios.post('/v2/documents/divisions', item);
  if (response.status === 201) return true;
  return false;
}

export async function removeDivisionsByIds(ids: string[]) {
  const response = await axios.post('/v2/documents/divisions/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status === 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getDocumentTypes(filter?: {
  canBeParent?: boolean;
  excludeIds?: string[];
  documentTypeId?: string;
}): Promise<DocumentType[]> {
  const response = await axios.post('/v2/documents/document-types/search', filter || {});
  return validate(getDocumentTypesSchema, response.data.data);
}

export async function getDocumentTypeById(id: string): Promise<DocumentType | null> {
  const response = await axios.get(`/v2/documents/document-types/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });

  if (response.status === 404) return null;

  const documentType = validate(getDocumentTypesSchema, [response.data]);
  return documentType[0];
}

export async function addOrEditDocumentType(item: DocumentType) {
  const response = await axios.post('/v2/documents/document-types', item);
  if (response.status === 201) return true;
  return false;
}

export async function removeDocumentTypesByIds(ids: string[]) {
  const response = await axios.post('/v2/documents/document-types/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status === 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getDocumentStatuses(): Promise<DocumentStatus[]> {
  const response = await axios.get('/v2/documents/document-statuses');
  return validate(getDocumentStatusesSchema, response.data.data);
}
export async function addOrEditDocumentStatus(item: DocumentStatus) {
  const response = await axios.post('/v2/documents/document-statuses', item);
  if (response.status === 201) return true;
  return false;
}
export async function removeDocumentStatusesByIds(ids: string[]) {
  const response = await axios.post('/v2/documents/document-statuses/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status === 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getPaymentTypes(): Promise<PaymentType[]> {
  const response = await axios.get('/v2/documents/payment-types');
  return validate(getPaymentTypesSchema, response.data.data);
}
export async function addOrEditPaymentType(item: PaymentType) {
  const response = await axios.post('/v2/documents/payment-types', item);
  if (response.status === 201) return true;
  return false;
}
export async function removePaymentTypesByIds(ids: string[]) {
  const response = await axios.post('/v2/documents/payment-types/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status === 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getDevices(): Promise<Device[]> {
  const response = await axios.get('/v2/documents/devices');
  return validate(getDevicesSchema, response.data.data);
}
export async function getDeviceById(id: string): Promise<Device | null> {
  const response = await axios.get(`/v2/documents/devices/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });

  if (response.status === 404) return null;

  const device = validate(getDevicesSchema, [response.data]);
  return device[0];
}
export async function addOrEditDevice(item: Device) {
  const response = await axios.post('/v2/documents/devices', item);
  if (response.status === 201) return true;
  return false;
}
export async function removeDevicesByIds(ids: string[]) {
  const response = await axios.post('/v2/documents/devices/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status === 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getAllDeviceConfigurations(): Promise<Partial<DeviceConfiguration>[]> {
  const response = await axios.get(`/v2/documents/device-configurations/`);
  return validate(getDeviceConfigurationBaseSchema, response.data.data);
}

export async function getDeviceConfigurationsByDeviceId(
  id: string
): Promise<DeviceConfiguration[]> {
  const response = await axios.get(`/v2/documents/device-configurations/${id}`);
  return validate(getDeviceConfigurationsSchema, response.data.data);
}
export async function addOrEditDeviceConfiguration(item: DeviceConfiguration) {
  const response = await axios.post('/v2/documents/device-configurations', item);
  if (response.status === 201) return true;
  return false;
}
export async function removeDeviceConfigurationsByIds(ids: string[]) {
  const response = await axios.post('/v2/documents/device-configurations/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status === 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getAllDocumentSeries(): Promise<DocumentSeries[]> {
  const response = await axios.get(`/v2/documents/document-series`);
  return validate(getDocumentSeriesSchema, response.data.data);
}

export async function getDocumentSeriesById(id: string): Promise<DocumentSeries | null> {
  const response = await axios.get(`/v2/documents/document-series/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });
  if (response.status === 404) return null;
  const documentSeries = validate(getDocumentSeriesSchema, [response.data]);
  return documentSeries[0];
}
export async function addOrEditDocumentSeries(item: DocumentSeries) {
  const response = await axios.post('/v2/documents/document-series', item);
  if (response.status === 201) return true;
  return false;
}
export async function removeDocumentSeriesByIds(ids: string[]) {
  const response = await axios.post('/v2/documents/document-series/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status === 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getDeliveryTypes(): Promise<DeliveryType[]> {
  const response = await axios.get('/v2/documents/delivery-types');
  return validate(getDeliveryTypesSchema, response.data.data);
}
export async function addOrEditDeliveryTypes(item: DeliveryType) {
  const response = await axios.post('/v2/documents/delivery-types', item);
  if (response.status === 201) return true;
  return false;
}
export async function removeDeliveryTypesByIds(ids: string[]) {
  const response = await axios.post('/v2/documents/delivery-types/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status === 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getAllDocumentSeriesTypes(): Promise<Partial<DocumentSeriesType>[]> {
  const response = await axios.get(`/v2/documents/document-series-types/`);
  return validate(getDocumentSeriesTypesBaseSchema, response.data.data);
}

export async function getDocumentSeriesTypes(docTypeId: string): Promise<DocumentSeriesType[]> {
  const response = await axios.get(`/v2/documents/document-series-types/${docTypeId}`);
  return validate(getDocumentSeriesTypesSchema, response.data.data);
}

export async function addOrEditDocumentSeriesType(item: DocumentSeriesType) {
  const response = await axios.post('/v2/documents/document-series-types', item);
  if (response.status === 201) return true;
  return false;
}

export async function removeDocumentSeriesTypesByIds(ids: string[]) {
  const response = await axios.post('/v2/documents/document-series-types/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status === 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getSalesDocumentsWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  excludeIDs,
  dateFrom,
  dateTo,
  documentStatusIds,
  documentTypeIds,
  salesPersonId,
  isActive,
  deleted,
}: SalesDocumentsFiltersData): Promise<{
  data: SalesDocumentForGrid[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/documents/sales-documents/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn,
      sortAscending,
      excludeIDs,
      dateFrom: dateFrom ? parseDateManually(dateFrom) : null,
      dateTo: dateTo ? parseDateManually(dateTo) : null,
      documentStatusIds,
      documentTypeIds,
      salesPersonId,
      isActive,
      deleted,
    }
  );

  const valid = validate(getSalesDocumentsWithConditionsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function removeSalesDocumentsByIds(ids: string[]) {
  const response = await axios.post('/v2/documents/sales-documents/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status === 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getSalesDocumentById(id: string): Promise<SalesDocument | null> {
  const response = await axios.get(`v2/documents/sales-documents/${id}`, {
    validateStatus: (status: number) => status === 404 || status == 200,
  });
  if (response.status === 404) return null;
  return validate(getSalesDocumentByIdSchema, response.data);
}

export async function addOrEditSalesDocument(document: SalesDocument) {
  const validRecordModes = [RecordMode.Added, RecordMode.Modified, RecordMode.Deleted];

  const filteredExpensesAndCharges =
    document.documentHeader?.documentHeaderExpensesAndCharges?.filter(
      (item) => item.recordMode && validRecordModes.includes(item.recordMode)
    ) ?? [];

  const filteredDetails =
    document.documentHeader?.documentDetails?.filter(
      (detail) => detail.recordMode && validRecordModes.includes(detail.recordMode)
    ) ?? [];

  const updateSalesDocument: SalesDocument = {
    ...document,
    deliveryTypeId: document.deliveryTypeId ?? null,

    documentHeader: {
      ...document.documentHeader,
      officialDate: document.documentHeader.officialDate
        ? datepickerToServerDateFormat(document.documentHeader.officialDate)
        : undefined,
      deliveryDate: document.documentHeader.deliveryDate
        ? datepickerToServerDateFormat(document.documentHeader.deliveryDate)
        : undefined,
      documentHeaderExpensesAndCharges: filteredExpensesAndCharges,
      documentDetails: filteredDetails,
    },
  };

  if (document.documentHeader?.documentPayment?.id) {
    updateSalesDocument.documentHeader.documentPayment = {
      id: document.documentHeader.documentPayment.id,
      paymentTypeId: document.documentHeader.documentPayment.paymentTypeId,
      startDate: new Date().toISOString(),
      endDate: new Date().toISOString(),
      deleted: document.documentHeader.documentPayment.deleted,
    };
  }
  const response = await axios.post('/v2/documents/sales-documents', updateSalesDocument);

  return {
    response: response.status === 201,
    error: response.data.isSuccess ? null : response.data?.error?.description,
  };
}

export async function getExpensesAndCharges(): Promise<ExpensesAndCharges[]> {
  const response = await axios.get('/v2/documents/expensesAndCharges');
  return validate(getExpensesAndChargesSchema, response.data.data);
}

export async function getMaterialRecentTransactions(
  id: string
): Promise<{ htmlContent: string; data: any[] } | null> {
  const response = await axios.get(`/v2/documents/${id}/recent-transactions`, {
    validateStatus: (status: number) => status === 404 || status == 200,
  });

  if (response.status === 404) return null;

  return response.data;
}

export async function getMyDataDocuments({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  excludeIDs,
  dateFrom,
  dateTo,
  divisionIds,
  documentSeriesIds,
  documentTypeIds,
  syncedSuccess,
  syncedFailure,
  unsynced,
  isActive,
}: MyDataDocumentsFiltersData): Promise<{
  data: MyDataDocumentHeader[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/documents/myData-documents/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn,
      sortAscending,
      excludeIDs,
      dateFrom: dateFrom ? parseDateManually(dateFrom) : null,
      dateTo: dateTo ? parseDateManually(dateTo) : null,
      divisionIds,
      documentSeriesIds,
      documentTypeIds,
      syncedSuccess,
      syncedFailure,
      unsynced,
      isActive,
    }
  );

  const valid = validate(getMyDataDocumentsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function uploadDocumentsToESA(ids: string[]) {
  const response = await axios.post('/v2/documents/myData-documents/send', ids);
  return response.data;
}

export async function getDocumentTypeColumnDefinitionFields(
  documentTypeId: string
): Promise<{ documentTypeCode: string; fieldName: string; documentTypeId: string }[]> {
  const response = await axios.get(
    `/v2/documents/document-types/column-definition-fields/${documentTypeId}`
  );
  return validate(getDocumentTypeColumnDefinitionFieldsSchema, response.data.value);
}

export async function setCustomerDefaultDocumentType(documentTypeId: string): Promise<any> {
  const response = await axios.post(
    `/v2/documents/document-types/set-customers-default/${documentTypeId}`
  );
  return response.data;
}

// Distribution Channels

export async function removeDistributionChannelsByIds(ids: string[]) {
  const response = await axios.post('/v2/documents/distribution-channels/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status === 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getAllDistributionChannels(): Promise<DistributionChannel[]> {
  const response = await axios.get(`/v2/documents/distribution-channels`);
  return validate(getDistributionChannelsSchema, response.data.data);
}

export async function getDistributionChannelById(id: string): Promise<DistributionChannel | null> {
  const response = await axios.get(`/v2/documents/distribution-channels/${id}`, {
    validateStatus: (status: number) => status == 404 || status == 200,
  });

  if (response.status === 404) return null;

  const distributionChannel = validate(getDistributionChannelsSchema, [response.data]);
  return distributionChannel[0];
}

export async function addOrEditDistributionChannel(item: DistributionChannel) {
  const response = await axios.post('/v2/documents/distribution-channels', item);
  if (response.status === 201) return true;
  return false;
}
